import { useMemo } from 'react';

import { useGetLoggedInUser } from '@revelio/auth';
import { PlotTooltipPortalProvider } from '@revelio/replots';

import type { EntityType } from '../CustomDash/utils/routing-helpers';
import { ChartRuntimeShell } from './chart-runtime-shell';
import { GlobalAIAssistantChartPortal } from './global-ai-assistant-chart-portal';
import { useGlobalAIAssistantOptional } from './global-ai-assistant-context';
import type { GlobalAiSingleChartTranscriptMessage } from './global-ai-assistant-transcript';
import { toGlobalAiInlineChartSpec } from './global-ai-assistant-transcript';
import { GlobalAiPortalLayer } from './global-ai-portal-layer';
import { useGlobalAIAssistantFeatureEnabled } from './use-global-ai-assistant-feature-enabled';

/**
 * Hosts one ChartRuntimeShell per live preview message id and portals the chart into the
 * drawer slot for that transcript row.
 */
export function GlobalAIAssistantChartPreviewShell() {
  const enabled = useGlobalAIAssistantFeatureEnabled();
  const ctx = useGlobalAIAssistantOptional();
  const { loggedInUser } = useGetLoggedInUser();

  const owner = useMemo(
    () => ({
      id: loggedInUser?.id ?? 'global-ai-preview',
      username: loggedInUser?.username ?? 'global-ai-preview',
    }),
    [loggedInUser?.id, loggedInUser?.username]
  );

  if (!enabled || !ctx) {
    return null;
  }

  const { transcript, chartPreviewMounts, liveChartPreviewMessageIds } = ctx;

  return (
    // Charts mount into the AI assistant drawer, whose body clips overflow
    // and sits below the rest of the app's stacking context. The portal
    // layer re-stamps Chakra portals (chart tooltips etc.) above the drawer
    // card, and the provider tells `PlotTooltip` to render through
    // `document.body` so chart tooltips aren't cut off by the drawer edge.
    <GlobalAiPortalLayer>
      <PlotTooltipPortalProvider>
        {liveChartPreviewMessageIds.map((messageId) => {
          const msg = transcript.find(
            (m): m is GlobalAiSingleChartTranscriptMessage =>
              m.id === messageId &&
              m.role === 'assistant' &&
              m.kind === 'single_chart'
          );
          const mount = chartPreviewMounts[messageId];
          if (!msg || !mount) {
            return null;
          }
          const spec = toGlobalAiInlineChartSpec(msg);
          return (
            <ChartRuntimeShell
              key={messageId}
              runtimeKey={messageId}
              owner={owner}
              entityType={msg.entityType as EntityType}
              chartItem={msg.item}
            >
              <GlobalAIAssistantChartPortal mount={mount} spec={spec} />
            </ChartRuntimeShell>
          );
        })}
      </PlotTooltipPortalProvider>
    </GlobalAiPortalLayer>
  );
}
